import { stringUtils } from "~/util";
import { sortByField, SORT_ORDER } from "../util";

const SET_PAGE = 'set-page';
const SET_PAGE_SIZE = 'set-page-size';
const SELECT_ROW = 'select-row';
const SET_DATA = 'set-data';
const SORT_DATA = 'sort-data';
const INITIALIZE = 'initialize';

export const ACTIONS = Object.freeze({
    SET_PAGE,
    SET_PAGE_SIZE,
    SELECT_ROW,
    SET_DATA,
    SORT_DATA,
    INITIALIZE
});

export function reducer(state, action) {
    switch (action.type) {

    case ACTIONS.INITIALIZE:
        return initialize(state, action);

    case ACTIONS.SET_PAGE:
        return updatePageIndex(state, action);

    case ACTIONS.SET_PAGE_SIZE:
        return updatePageSize(state, action);

    case ACTIONS.SELECT_ROW:
        return updateRowSelection(state, action);

    case ACTIONS.SET_DATA:
        return updateData(state, action);

    case ACTIONS.SORT_DATA:
        return sortData(state, action);

    default:
        return state;
    }
}

export function initialize(_, { payload }) {
    const {
        pageSize,
        pageIndex,
        data,
        columns,
        selectedRow,
        sort
    } = payload;

    const currentPage = getPage(data, pageIndex, pageSize);
    const _columns = deriveColumnKeys(columns);
    const columnMap = buildColumnMap(_columns);

    let state = {
        raw: data,
        data: [...data],
        columns: _columns,
        columnMap,
        sort,
        currentPage,
        pageSize,
        pageIndex,
        selectedRow
    };

    if (stringUtils.hasValue(sort?.column) && columnMap.has(sort.column)) {
        return sortData(state, { payload: { ...sort }});
    }

    return state;
}

/**
 * Builds a map from column definitions, for easier access later
 * 
 * @param {any[]} columns An array of column definitions
 * @returns {Map}
 */
export function buildColumnMap(columns = []) {
    let map = new Map(); 

    for (let col of columns) {       
        if (map.has(col.key)) {
            let dupeKeyError = new Error(`Duplicate column key found - ${col.key}`);
            dupeKeyError.innerMessage = 'Every column should have a unique `key`, `field`, or `title` property';
            throw dupeKeyError;
        } else {
            map.set(col.key, { ...col });
        }
    }

    return map;
}

/**
 * Ensures every columns has a `key` property, whether explicitly set or derived from `field` or `title`
 * @param {any[]} columns An array of column definitions
 * @returns {any[]}
 */
export function deriveColumnKeys(columns = []) {
    return columns.map(c => {
        const { key = '', title = '', field = null } = c;

        if (stringUtils.hasValue(`${key}`)) {
            return c;
        } else {
            if (stringUtils.hasValue(field)) {                
                return { ...c, key: field };
            } else if (stringUtils.hasValue(title)) {
                return { ...c, key: title };
            } else {
                let noKeyError = new Error(`Unable to determine key for column - ${JSON.stringify(c)}`);
                noKeyError.innerMessage = 'Every column should have a unique `key`, `field`, or `title` property';
                throw noKeyError;
            }
        }
    });
}

function updatePageIndex(state, action) {
    const { pageIndex } = action.payload;
    const { pageSize } = state;

    const page = getPage([...state.data], pageIndex, pageSize);

    return {
        ...state,
        currentPage: page,
        pageIndex: pageIndex
    };
}

function updatePageSize(state, action) {
    const {
        pageSize: currentPageSize,
        pageIndex: currentPageIndex,
        data
    } = state;

    const { pageSize } = action.payload;

    const pageIndex = Math.floor(currentPageIndex * (pageSize / currentPageSize));

    const page = getPage([...data], pageIndex, pageSize);

    return {
        ...state,
        pageSize,
        pageIndex,
        currentPage: page
    };
}

function getPage(arr, pageIndex, pageSize) {
    const start = pageSize * pageIndex;
    const end = start + pageSize;

    return arr.slice(start, end);
}

function updateRowSelection(state, action) {
    const { selectedRow } = state;
    const { index } = action.payload;

    const newSelection = selectedRow === index ? -1 : index;

    return {
        ...state,
        selectedRow: newSelection
    };
}

function updateData(state, action) {
    const { data } = action.payload;

    const page = getPage(data, state.pageIndex, state.pageSize);

    return {
        ...state,
        raw: data,
        data: [...data],
        currentPage: page
    };
}

function sortData(state, { payload }) {
    const { column, order } = payload;
    const { 
        raw,  
        columnMap,
        pageSize
    } = state;

    if (order == SORT_ORDER.NONE || !stringUtils.hasValue(column)) {
        const currentPage = getPage(raw, 0, pageSize);

        return {
            ...state,
            currentPage,
            pageIndex: 0,
            data: [ ...raw ],
            sort: { ...payload }
        };
    }

    const sortColumn = columnMap.get(column);

    const sorter = typeof sortColumn.sorter == 'function'
        ? sortColumn.sorter
        : sortByField(sortColumn.field);

    let sorted = [ ...raw ].sort(sorter);

    if (order == SORT_ORDER.DESC)
        sorted = sorted.reverse();

    const currentPage = getPage(sorted, 0, pageSize);

    return {
        ...state,
        data: sorted,
        currentPage,
        pageIndex: 0,
        sort: { ...payload }
    };
}